import type {
  CornerEnumKey,
  LayoutBlock,
  PageMeta,
  SiteMode,
  SpacingEnumKey,
  TypographyEnumKey,
} from '@web-studio/shared-schemas';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MatrixCanvas } from '@/components/MatrixCanvas';
import { useLabCaptureMode } from '@/hooks/useLabCaptureMode';
import type { MatrixLocale } from '@web-studio/core-fixtures';

export interface BootstrapCapturePayload {
  blocks: LayoutBlock[];
  themeId: string;
  colorsetId: string;
  spacingEnum: SpacingEnumKey;
  /** Theme registry defaults — same enums as onboarding project create. */
  cornerEnum: CornerEnumKey;
  typographyEnum: TypographyEnumKey;
  locale: MatrixLocale;
  viewport: 'desktop' | 'tablet' | 'mobile';
  /** Nav IA — same as sandbox / project bootstrap (avoids restaurant Speisekarte default). */
  vertical: string;
  siteMode: SiteMode;
  pages: PageMeta[];
  defaultLocale: string;
  supportedLocales: string[];
}

declare global {
  interface Window {
    __WS_BOOTSTRAP_CAPTURE__?: BootstrapCapturePayload;
  }
}

/**
 * Capture ingress for marketplace / UI-review Playwright.
 * Blocks are injected via `page.addInitScript` from the same bootstrap builders
 * used at project create — not hand-authored Design Lab fixtures.
 */
export function BootstrapCapturePage() {
  const captureMode = useLabCaptureMode();
  const [searchParams] = useSearchParams();
  const [payload] = useState<BootstrapCapturePayload | null>(
    () => (typeof window !== 'undefined' ? (window.__WS_BOOTSTRAP_CAPTURE__ ?? null) : null),
  );

  const viewportParam = searchParams.get('viewport');
  const viewport =
    viewportParam === 'mobile' || viewportParam === 'tablet' ? viewportParam : 'desktop';

  const view = useMemo(() => {
    if (!payload) return null;
    return {
      ...payload,
      viewport: payload.viewport ?? viewport,
    };
  }, [payload, viewport]);

  if (!captureMode) {
    return (
      <p className="text-sm" style={{ color: 'var(--lab-muted)', padding: '1.5rem' }}>
        Bootstrap capture is a Playwright ingress (`capture=1` + injected homepage blocks). Use
        Package layout for interactive preview.
      </p>
    );
  }

  if (!view?.blocks.length) {
    return (
      <p className="text-sm" style={{ color: 'var(--lab-muted)', padding: '1.5rem' }}>
        Waiting for bootstrap blocks (`window.__WS_BOOTSTRAP_CAPTURE__`).
      </p>
    );
  }

  return (
    <div className="lab-capture-stage">
      <MatrixCanvas
        blocks={view.blocks}
        locale={view.locale}
        themeId={view.themeId}
        colorsetId={view.colorsetId}
        spacingEnum={view.spacingEnum}
        cornerEnum={view.cornerEnum}
        typographyEnum={view.typographyEnum}
        viewport={view.viewport}
        pages={view.pages}
        defaultLocale={view.defaultLocale}
        supportedLocales={view.supportedLocales}
        siteMode={view.siteMode}
        vertical={view.vertical}
      />
    </div>
  );
}
